<template>
  <div class="payment-status-kpis">
    <!-- Total Summary (optional) -->
    <div v-if="showTotalSummary && metrics" class="total-summary">
      <div class="total-summary-card">
        <div class="total-summary-label">{{ t('Total de Compromissos') }}</div>
        <div class="total-summary-value">{{ metrics.total }}</div>
      </div>
      <div class="total-summary-divider"></div>
      <div class="total-summary-card">
        <div class="total-summary-label">{{ t('Valor Total') }}</div>
        <div class="total-summary-value">{{ n(parseFloat(String(metrics.totalValor)) || 0, 'currency') }}</div>
      </div>
    </div>

    <!-- KPI Cards -->
    <div v-if="metrics" class="dashboard-grid" :class="gridClass">
      <box class="kpi-card kpi-danger">
        <div class="kpi-title">
          <i class="pi pi-exclamation-circle"></i>
          {{ t('Pendentes') }}
        </div>
        <div class="kpi-content">
          <div class="kpi-value">{{ metrics.due }}</div>
          <div class="kpi-label">{{ calculatePercentage(metrics.due, metrics.total) }}% {{ t('total') }}</div>
          <div class="kpi-amount">{{ n(parseFloat(String(metrics.dueValor)) || 0, 'currency') }}</div>
        </div>
      </box>

      <box class="kpi-card kpi-warning">
        <div class="kpi-title">
          <i class="pi pi-clock"></i>
          {{ t('Parcial') }}
        </div>
        <div class="kpi-content">
          <div class="kpi-value">{{ metrics.partial }}</div>
          <div class="kpi-label">{{ calculatePercentage(metrics.partial, metrics.total) }}% {{ t('total') }}</div>
          <div class="kpi-amount">{{ n(parseFloat(String(metrics.partialValor)) || 0, 'currency') }}</div>
        </div>
      </box>

      <box class="kpi-card kpi-success">
        <div class="kpi-title">
          <i class="pi pi-check-circle"></i>
          {{ t('Pagos') }}
        </div>
        <div class="kpi-content">
          <div class="kpi-value">{{ metrics.complete }}</div>
          <div class="kpi-label">{{ calculatePercentage(metrics.complete, metrics.total) }}% {{ t('total') }}</div>
          <div class="kpi-amount">{{ n(parseFloat(String(metrics.completeValor)) || 0, 'currency') }}</div>
        </div>
      </box>
    </div>

    <!-- Loading State -->
    <div v-else-if="loading" class="loading-state">
      <i class="pi pi-spin pi-spinner"></i>
      <span>{{ t('Carregando métricas...') }}</span>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <i class="pi pi-chart-bar"></i>
      <span>{{ t('Nenhuma métrica disponível') }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

interface PaymentMetrics {
  total: number;
  totalValor: number | string;
  due: number;
  dueValor: number | string;
  partial: number;
  partialValor: number | string;
  complete: number;
  completeValor: number | string;
}

const props = defineProps<{
  metrics?: PaymentMetrics | null;
  showTotalSummary?: boolean;
  loading?: boolean;
  compact?: boolean;
}>();

const { t, n } = useI18n();

// Computed class for grid layout
const gridClass = computed(() => ({
  'compact-grid': props.compact
}));

// Calculate percentage
const calculatePercentage = (value: number, total: number): number => {
  if (!total || total === 0) return 0;
  return Math.round((value / total) * 100);
};
</script>

<style scoped>
.payment-status-kpis {
  width: 100%;
}

/* Total Summary */
.total-summary {
  display: flex;
  justify-content: center;
  align-items: stretch;
  gap: 0;
  padding: 1.5rem 2rem;
  margin-bottom: 1.5rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  border: 1px solid var(--surface-border);
}

.total-summary-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 0 2rem;
}

.total-summary-divider {
  width: 1px;
  background: var(--surface-border);
  margin: 0.5rem 0;
}

.total-summary-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-color-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.total-summary-value {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-color);
  line-height: 1;
}

/* Dashboard Grid */
.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.dashboard-grid.compact-grid {
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-bottom: 1rem;
}

.kpi-card {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  transition: transform 0.2s, box-shadow 0.2s;
}

.kpi-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.compact-grid .kpi-card:hover {
  transform: translateY(-1px);
}

.kpi-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color-secondary);
}

.kpi-title i {
  font-size: 1.25rem;
}

.compact-grid .kpi-title {
  font-size: 0.875rem;
}

.compact-grid .kpi-title i {
  font-size: 1rem;
}

.kpi-content {
  padding: 0.5rem 0;
}

.compact-grid .kpi-content {
  padding: 0.25rem 0;
}

.kpi-value {
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary-color);
  line-height: 1;
  margin-bottom: 0.25rem;
}

.compact-grid .kpi-value {
  font-size: 1.75rem;
}

.kpi-label {
  font-size: 0.875rem;
  color: var(--text-color-secondary);
  margin-bottom: 0.5rem;
}

.compact-grid .kpi-label {
  font-size: 0.75rem;
  margin-bottom: 0.25rem;
}

.kpi-amount {
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-color);
}

.compact-grid .kpi-amount {
  font-size: 0.875rem;
}

/* KPI Card Variants */
.kpi-success .kpi-value {
  color: #22c55e;
}

.kpi-success .kpi-title i {
  color: #22c55e;
}

.kpi-warning .kpi-value {
  color: #f59e0b;
}

.kpi-warning .kpi-title i {
  color: #f59e0b;
}

.kpi-danger .kpi-value {
  color: #ef4444;
}

.kpi-danger .kpi-title i {
  color: #ef4444;
}

/* Loading State */
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 2rem;
  color: var(--text-color-secondary);
}

.loading-state i {
  font-size: 1.5rem;
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem;
  color: var(--text-color-secondary);
}

.empty-state i {
  font-size: 2rem;
  opacity: 0.5;
}

/* Responsive */
@media (max-width: 768px) {
  .total-summary {
    flex-direction: column;
    gap: 1.5rem;
    padding: 1.25rem 1rem;
  }

  .total-summary-card {
    padding: 0;
  }

  .total-summary-divider {
    width: 100%;
    height: 1px;
    margin: 0;
  }

  .total-summary-value {
    font-size: 1.75rem;
  }

  .dashboard-grid,
  .dashboard-grid.compact-grid {
    grid-template-columns: 1fr;
  }
}
</style>
